import React from "react";
import './feed.css';
import { useCallback, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import CITIES from "../../utils/cities";
import useTour from "../../tour/useTour";
import feedSteps from "../../tour/steps/feedSteps";
import TourButton from "../../components/tourButton/TourButton";
import usePullToRefresh from "../../hooks/usePullToRefresh";

const TOP_CITIES = ["Lahore", "Islamabad", "Rawalpindi", "Peshawar", "Kamra", "Sargodha"];

const Feed = ( { isLoggedIn, loginUser, onLogout } ) => {
  const [feedDetails, setFeedDetails] = useState([]);
  const [finderError, setFinderError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isRideOnly] = useState(false);
  const [activeCities, setActiveCities] = useState([]);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const [allRides, setAllRides] = useState([]);

  const getAllRides = useCallback(async (page = 1) => {
    try {
      if (isRideOnly) {
        const response = await axios.get('/api/v1/ride/all', { params: { page, limit: 10 } });
        setFeedDetails(response.data.rides);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.page || 1);
      } else {
        const response = await axios.get('/api/v1/ride/requests/all', { params: { page, limit: 10 } });
        setFeedDetails(response.data.ridesAndRequests);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.page || 1);
      }
      setFinderError("");
      setAllRides([]);
      setSortBy("newest");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else if (error.response) {
        setFinderError(error.response.data.message);
        setFeedDetails([]);
      } else {
        setFinderError("An error occurred while fetching rides.");
        setFeedDetails([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isRideOnly, onLogout, navigate]);

  const handleRefresh = useCallback(async () => {
    setActiveCities([]);
    await getAllRides(1);
  }, [getAllRides]);

  const { isRefreshing, pullDistance, containerRef } = usePullToRefresh(handleRefresh);

  const { startTour } = useTour("feed", feedSteps, !loading && feedDetails.length > 0);

  useEffect(() => {
    if (activeCities.length > 0) {
      return;
    }

    let intervalId;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !intervalId) {
        intervalId = setInterval(() => getAllRides(currentPage), 30000);
      } else if (document.visibilityState !== 'visible' && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    getAllRides(currentPage);

    if (document.visibilityState === 'visible') {
      intervalId = setInterval(() => getAllRides(currentPage), 30000);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeCities, isRideOnly, getAllRides, currentPage]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    } else {
      localStorage.setItem("lastSeenFeed", new Date().toISOString());
    }
  }, [isLoggedIn, navigate]);

  // Close search dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const extraCities = activeCities.filter((c) => !TOP_CITIES.includes(c));

  const searchResults = searchQuery.length > 0
    ? CITIES.filter((c) =>
        c.toLowerCase().startsWith(searchQuery.toLowerCase()) &&
        !activeCities.includes(c)
      )
    : [];

  const handleCityClick = async (city) => {
    const updated = activeCities.includes(city)
      ? activeCities.filter((c) => c !== city)
      : [...activeCities, city];

    setActiveCities(updated);
    setFinderError("");

    if (updated.length === 0) {
      setCurrentPage(1);
      getAllRides(1);
      return;
    }

    try {
      const response = await axios.get('/api/v1/ride/filterRide/', {
        params: { location: updated.join(",") }
      });
      setFeedDetails(response.data.filteredRides);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else if (error.response) {
        setFinderError(error.response.data.message);
        setFeedDetails([]);
      } else {
        setFinderError("An error occurred while filtering rides.");
      }
    }
  };

  const handleSearchSelect = (city) => {
    handleCityClick(city);
    setSearchQuery("");
    setSearchOpen(false);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);

    const source = value === "today" && allRides.length > 0 ? [...allRides] : [...feedDetails];

    if (value === "today") {
      if (allRides.length === 0) setAllRides([...feedDetails]);
      const now = new Date();
      const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const filtered = source.filter((r) => {
        const dep = new Date(r.estimatedDate);
        return dep >= now && dep <= in24h;
      });
      filtered.sort((a, b) => new Date(a.estimatedDate) - new Date(b.estimatedDate));
      setFeedDetails(filtered);
      return;
    }

    const base = allRides.length > 0 ? [...allRides] : [...feedDetails];
    if (allRides.length > 0) setAllRides([]);

    const sorted = base;
    if (value === "newest") {
      sorted.sort((a, b) => new Date(b.estimatedDate) - new Date(a.estimatedDate));
    } else if (value === "oldest") {
      sorted.sort((a, b) => new Date(a.estimatedDate) - new Date(b.estimatedDate));
    }
    setFeedDetails(sorted);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      getAllRides(newPage);
    }
  };

  const formatRideDate = (estimatedDate) => {
    const date = new Date(estimatedDate);
    const oneDayInMs = 24 * 60 * 60 * 1000;

    if (Math.abs(date - Date.now()) < oneDayInMs) {
      return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'});
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  return (
    <div className="sr-feed" ref={containerRef}>
      {/* ── Dark top section with rounded bottom ── */}
      <div className="sr-feed__top">
        <div className="sr-feed__top-content">
          <div className="sr-feed__header">
            <h1 className="sr-feed__title">{isRideOnly ? 'Recent Rides' : 'Rides & Requests'}</h1>
            <p className="sr-feed__subtitle">Find your next carpool</p>
          </div>

        </div>
      </div>

      {/* ── White content section ── */}
      <div className="sr-feed__body">
        {/* ── Filter bar + Sort ── */}
        <div className="sr-feed__sort-bar">
          <span className="sr-feed__sort-label">
            Active filters: {activeCities.length > 0 ? <><strong>{activeCities.length}</strong> <button className="sr-feed__sort-clear" onClick={() => { setActiveCities([]); setCurrentPage(1); getAllRides(1); }}>Clear</button></> : "None"}
          </span>
          <select className="sr-feed__sort-select" data-tour="feed-sort" value={sortBy} onChange={handleSortChange}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="today">Leaving today</option>
          </select>
        </div>

        {/* ── City Search ── */}
        <div className="sr-feed__search" ref={searchRef} data-tour="feed-chips">
          <div className="sr-feed__search-bar">
            <svg className="sr-feed__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="sr-feed__search-input"
              type="text"
              placeholder="Search city..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => { if (searchQuery.length > 0) setSearchOpen(true); }}
            />
          </div>
          {searchOpen && searchResults.length > 0 && (
            <div className="sr-feed__search-dropdown">
              {searchResults.map((city) => {
                const matchLen = searchQuery.length;
                return (
                  <button
                    key={city}
                    className="sr-feed__search-item"
                    onClick={() => handleSearchSelect(city)}
                  >
                    <span className="sr-feed__search-match">{city.slice(0, matchLen)}</span>
                    {city.slice(matchLen)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Quick Chips + Extra ── */}
        <div className="sr-feed__chips">
          {extraCities.map((city) => (
            <button
              key={city}
              className="sr-feed__chip sr-feed__chip--active sr-feed__chip--extra"
              onClick={() => handleCityClick(city)}
            >
              {city} <span className="sr-feed__chip-close">&times;</span>
            </button>
          ))}
          {TOP_CITIES.map((city) => (
            <button
              key={city}
              className={`sr-feed__chip ${activeCities.includes(city) ? 'sr-feed__chip--active' : ''}`}
              onClick={() => handleCityClick(city)}
            >
              {city}
            </button>
          ))}
        </div>

        {(pullDistance > 0 || isRefreshing) && (
          <div className="sr-feed__pull-indicator" style={{ height: pullDistance }}>
            {isRefreshing ? "Refreshing..." : "Pull to refresh"}
          </div>
        )}

        {/* ── Ride List ── */}
        <div className="sr-feed__rides">
          {finderError !== "" && <div className="sr-feed__error">{finderError}</div>}

          {loading && feedDetails.length === 0 && finderError === "" &&
            [1, 2, 3, 4, 5].map((i) => (
              <div className="sr-feed__card sr-feed__card--skeleton" key={i}>
                <div className="sr-feed__card-dots">
                  <span className="sr-feed__card-dot sr-feed__card-dot--from" />
                  <span className="sr-feed__card-connector" />
                  <span className="sr-feed__card-dot sr-feed__card-dot--to" />
                </div>
                <div className="sr-feed__card-route">
                  <div className="sr-feed__card-cities">
                    <div className="sr-feed__skeleton sr-feed__skeleton--city" />
                    <div className="sr-feed__skeleton sr-feed__skeleton--city sr-feed__skeleton--city-short" />
                  </div>
                  <div className="sr-feed__card-meta">
                    <div className="sr-feed__skeleton sr-feed__skeleton--meta" />
                    <div className="sr-feed__skeleton sr-feed__skeleton--meta sr-feed__skeleton--meta-short" />
                  </div>
                </div>
                <div className="sr-feed__card-user">
                  <div className="sr-feed__skeleton sr-feed__skeleton--avatar" />
                  <div className="sr-feed__skeleton sr-feed__skeleton--username" />
                </div>
              </div>
            ))
          }

          {!loading && finderError === "" && feedDetails.length === 0 && (
            <div className="sr-feed__empty">
              <p className="sr-feed__empty-text">No rides found. Be the first to publish one!</p>
              <Link to="/publish" className="sr-feed__empty-btn">Publish a Ride</Link>
            </div>
          )}

          {finderError === "" &&
            (() => {
              // Find the most popular ride (highest popularityScore)
              const mostPopularId = feedDetails.length > 0
                ? feedDetails.reduce((best, r) => (r.popularityScore || 0) > (best.popularityScore || 0) ? r : best, feedDetails[0])._id
                : null;
              // Only show "Most Popular" if it actually has engagement
              const hasMostPopular = feedDetails.some(r => r._id === mostPopularId && (r.popularityScore || 0) > 0);

              // Sort: most popular first, then original order
              const sorted = [...feedDetails].sort((a, b) => {
                if (hasMostPopular && a._id === mostPopularId) return -1;
                if (hasMostPopular && b._id === mostPopularId) return 1;
                return 0;
              });

              return sorted.map((element, idx) => {
                const { _id, user, arrival, departure, route, estimatedDate, isStarted, isOfferOrRequest, fare, totalSeats } = element;
                const isMostPopular = hasMostPopular && _id === mostPopularId;

                return (
                  <Link to={`/ride/${_id}`} className={`sr-feed__card${isStarted ? ' sr-feed__card--transit' : ''}`} key={_id} {...(idx === 0 ? { "data-tour": "feed-card" } : {})}>
                    {/* Type strip */}
                    <div className={`sr-feed__card-strip ${isOfferOrRequest ? 'sr-feed__card-strip--offer' : 'sr-feed__card-strip--request'}`}>
                      <span>{isOfferOrRequest ? "Offer" : "Request"}</span>
                      {isMostPopular && <span className="sr-feed__card-popular">Most Popular</span>}
                    </div>

                    {/* Card inner content */}
                    <div className="sr-feed__card-inner">
                      {/* Route dots */}
                      <div className="sr-feed__card-dots">
                        <span className="sr-feed__card-dot sr-feed__card-dot--from" />
                        <span className="sr-feed__card-connector" />
                        <span className="sr-feed__card-dot sr-feed__card-dot--to" />
                      </div>

                      {/* Route info */}
                      <div className="sr-feed__card-route">
                        <div className="sr-feed__card-cities">
                          <span className="sr-feed__card-city">{departure}</span>
                          {route && route !== "nil" && (
                            <span className="sr-feed__card-via">
                              {route.length > 18 ? `${route.substring(0, 15)}...` : route}
                            </span>
                          )}
                          <span className="sr-feed__card-city">{arrival}</span>
                        </div>
                      </div>

                      {/* User avatar */}
                      <div className="sr-feed__card-user" onClick={(e) => { e.preventDefault(); navigate(`/profile/${user._id}`); }}>
                        <div className="sr-feed__card-avatar-wrap">
                          <img src={user.avatar} alt="" className="sr-feed__card-avatar" />
                          {user.role === "admin" && (
                            <span className="sr-feed__card-badge-icon sr-feed__card-badge-icon--admin" title="Admin">&#9733;</span>
                          )}
                          {user.role !== "admin" && user.isApproved && (
                            <span className="sr-feed__card-badge-icon sr-feed__card-badge-icon--trusted" title="Admin Verified">&#10003;</span>
                          )}
                        </div>
                        <span className="sr-feed__card-username">{user.userName.split(' ')[0]}</span>
                      </div>
                    </div>

                    {/* Bottom info row */}
                    <div className="sr-feed__card-meta">
                      <span className="sr-feed__card-meta-item">
                        {isStarted ? "In Transit" : formatRideDate(estimatedDate)}
                      </span>
                      {isOfferOrRequest && fare && fare.amount != null && (
                        <span className="sr-feed__card-meta-item">
                          {fare.currency || "PKR"} {fare.amount}
                        </span>
                      )}
                      {isOfferOrRequest && totalSeats != null && (
                        <span className="sr-feed__card-meta-item">
                          {totalSeats} {totalSeats === 1 ? "seat" : "seats"}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              });
            })()
          }
        </div>

        {/* ── Pagination ── */}
        {activeCities.length === 0 && totalPages > 1 && (
          <div className="sr-feed__pagination" data-tour="feed-pagination">
            <button className="sr-feed__pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>Prev</button>
            <span className="sr-feed__pagination-text">{currentPage} / {totalPages}</span>
            <button className="sr-feed__pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>Next</button>
          </div>
        )}
      </div>
      <TourButton onClick={startTour} />
    </div>
  );
}

export default Feed;
